import { canonicalize } from './canonical';
import { hmacSha256Hex, sha256Hex } from './crypto';
import { DEMO_SIGNING_SECRET } from './constants';
import type { DemoWebhookPayload } from './types';

/**
 * Runtime guard for the demo webhook payload shape. The envelope schema
 * (contracts/input.schema.json) intentionally leaves `payload` open
 * (`type: object, maxProperties: 200`), so this project-level validation
 * happens in the domain layer and reports typed, path-aware errors.
 */
export function validateDemoWebhookPayload(
  payload: unknown,
): { ok: true } | { ok: false; paths: string[] } {
  const paths: string[] = [];
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return { ok: false, paths: ['payload'] };
  }
  const p = payload as Record<string, unknown>;
  if (typeof p.eventId !== 'string' || p.eventId.length < 1 || p.eventId.length > 100) {
    paths.push('payload.eventId');
  }
  if (typeof p.resourceId !== 'string' || p.resourceId.length < 1 || p.resourceId.length > 100) {
    paths.push('payload.resourceId');
  }
  if (typeof p.sequence !== 'number' || !Number.isInteger(p.sequence) || p.sequence < 0) {
    paths.push('payload.sequence');
  }
  if (typeof p.occurredAt !== 'string' || Number.isNaN(Date.parse(p.occurredAt))) {
    paths.push('payload.occurredAt');
  }
  if (
    typeof p.idempotencyKey !== 'string' ||
    p.idempotencyKey.length < 1 ||
    p.idempotencyKey.length > 100
  ) {
    paths.push('payload.idempotencyKey');
  }
  if (typeof p.timestamp !== 'number' || !Number.isFinite(p.timestamp)) {
    paths.push('payload.timestamp');
  }
  if (typeof p.signature !== 'string' || p.signature.length < 1) {
    paths.push('payload.signature');
  }
  if (
    p.failureKind !== undefined &&
    p.failureKind !== 'transient' &&
    p.failureKind !== 'permanent'
  ) {
    paths.push('payload.failureKind');
  }
  if (typeof p.data !== 'object' || p.data === null || Array.isArray(p.data)) {
    paths.push('payload.data');
  }
  return paths.length > 0 ? { ok: false, paths } : { ok: true };
}

export function asDemoWebhookPayload(payload: unknown): DemoWebhookPayload {
  return payload as DemoWebhookPayload;
}

/** Signature base string, Stripe-style: `${timestamp}.${sha256(canonical(data))}`. */
export async function buildSignatureBase(
  timestamp: number,
  data: Record<string, unknown>,
): Promise<string> {
  const dataHash = await sha256Hex(canonicalize(data));
  return `${timestamp}.${dataHash}`;
}

export async function signDemoPayload(
  timestamp: number,
  data: Record<string, unknown>,
): Promise<string> {
  const base = await buildSignatureBase(timestamp, data);
  return hmacSha256Hex(DEMO_SIGNING_SECRET, base);
}

export async function fingerprintPayloadData(data: Record<string, unknown>): Promise<string> {
  return sha256Hex(canonicalize(data));
}
